import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MarkdownModule } from 'ngx-markdown';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { AiPipeline, AiRun, AiRunRealtimeEvent } from '../../core/models/ai.model';
import { AI_REJECT_CODES, parseAiHttpError, sortRunSteps } from '../../core/utils/ai-form.util';
import {
  FINISHED_RUN,
  applyRunEvent,
  applyStepEvent,
  shouldApplyRealtimeEvent,
  shouldRefetchRunDetail,
} from '../../core/utils/ai-realtime.util';
import {
  AiAlertComponent,
  AiFormDialogComponent,
  AiFormSectionComponent,
  AiStatusBadgeComponent,
} from '../../shared';
import {
  AiRunFeedbackComponent,
  AiRunFeedbackSaved,
} from '../ai-run-feedback/ai-run-feedback.component';
import { rejectionInstruction } from '../ai-run-feedback/ai-run-feedback.util';
import { AiResultExportComponent } from '../result-export/ai-result-export.component';
import { AiRunStepTimelineComponent } from '../run-step-timeline/run-step-timeline.component';
import { AiRunnerResultDirective } from './ai-runner-result.directive';
import { loadRememberedOptions, saveRememberedOptions } from './ai-runner-storage.util';
import { AiRunnerSlotDirective, AiRunnerSlotName } from './ai-runner-slot.directive';
import { AI_RUNNER_ACCESS, resolveAiRunnerAccess } from './ai-runner-access';
import { AI_RUNNER_BACKEND } from './ai-runner-backend';
import {
  AI_RUNNER_DEFAULT_LABELS,
  AI_RUNNER_DEFAULTS,
  AI_RUNNER_DEFAULTS_VALUE,
  AI_RUNNER_LABELS,
  AiRunnerDefaults,
  AiRunnerLabels,
} from './ai-runner-labels';
import {
  buildFieldViews,
  buildPipelineVariants,
  buildRunInput,
  extractResultJson,
  fileToRunnerFile,
  findVariant,
  initialOptionValues,
  pipelineRef,
  previewBindings,
  resolveActionStates,
  validateOptions,
  visibleOptions,
  resultText,
  validateFiles,
} from './ai-runner-input.util';
import {
  AiChatMessage,
  AiResultActionState,
  AiResultBindingPreview,
  AiResultContext,
  AiResultFormat,
  AiRunnerChoice,
  AiRunnerConfig,
  AiRunnerSlotContext,
  AiRunnerText,
  AiRunnerData,
  AiRunnerInput,
  AiRunnerOptionValue,
  AiRunnerOptionValues,
  AiRunnerPipelineRef,
  AiRunnerVariant,
} from './ai-runner.model';

type Phase = 'input' | 'running' | 'done' | 'error';

/**
 * Modal universal para disparar a IA (chamado 0000579137); a tarefa inteira vem em `config`.
 * Reverb: inscreve em `ia-runs-{grupo}` só com o modal aberto E execução em andamento (`onCleanup`);
 * sem aviso em `pollFallbackMs`, consulta a execução. Arquivos vão em base64 no `input`, sem anexo.
 * Não conhece o domínio de quem chama: só executa os callbacks recebidos.
 */
@Component({
  selector: 'ai-runner-dialog',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    JsonPipe,
    FormsModule,
    MarkdownModule,
    SelectModule,
    AutoCompleteModule,
    CheckboxModule,
    InputNumberModule,
    MultiSelectModule,
    TooltipModule,
    AiAlertComponent,
    AiFormDialogComponent,
    AiFormSectionComponent,
    AiStatusBadgeComponent,
    AiRunStepTimelineComponent,
    AiRunFeedbackComponent,
    AiResultExportComponent,
  ],
  templateUrl: './ai-runner-dialog.component.html',
  styleUrls: ['../../shared/ai-form.scss', './ai-runner-dialog.component.scss'],
})
export class AiRunnerDialogComponent {
  private readonly backend = inject(AI_RUNNER_BACKEND);

  private readonly accessPolicy = inject(AI_RUNNER_ACCESS);
  private readonly labelsOverride = inject(AI_RUNNER_LABELS);
  private readonly defaultsOverride = inject(AI_RUNNER_DEFAULTS);

  readonly visible = input(false);
  readonly config = input.required<AiRunnerConfig>();
  readonly visibleChange = output<boolean>();
  readonly runStarted = output<AiRun>();
  readonly completed = output<AiRun>();
  readonly failed = output<AiRun>();
  /** Atualizações da tela aplicadas (chaves de `resultBindings`). */
  readonly resultApplied = output<{ run: AiRun; keys: string[] }>();
  /** Ações sobre o resultado concluídas com sucesso (chaves de `resultActions`). */
  readonly actionsApplied = output<{ run: AiRun; keys: string[] }>();

  /** Textos: padrão ← token ← `config.labels`. */
  readonly t = computed<AiRunnerLabels>(() => ({
    ...AI_RUNNER_DEFAULT_LABELS,
    ...this.labelsOverride,
    ...this.config().labels,
  }));
  /** Defaults de comportamento: padrão ← token. */
  readonly d = computed<AiRunnerDefaults>(() => ({
    ...AI_RUNNER_DEFAULTS_VALUE,
    ...this.defaultsOverride,
  }));

  private readonly slots = contentChildren(AiRunnerSlotDirective);
  private readonly slotTemplates = computed(
    () => new Map(this.slots().map(s => [s.name(), s.template]))
  );
  slot(name: AiRunnerSlotName) {
    return this.slotTemplates().get(name) ?? null;
  }

  readonly slotContext = computed<AiRunnerSlotContext>(() => ({
    config: this.config(),
    phase: this.phase(),
    run: this.run(),
    variant: this.variant(),
    percent: this.percent(),
    error: this.error(),
    submit: () => void this.submit(),
    cancel: () => void this.cancel(),
    close: () => this.close(),
  }));
  private text(value: AiRunnerText | undefined): string {
    return typeof value === 'function' ? value(this.slotContext()) : (value ?? '');
  }
  readonly titleText = computed(() => this.text(this.config().title));
  readonly descriptionText = computed(() => this.text(this.config().description));
  readonly helpText = computed(() => this.text(this.config().help));

  readonly resultTemplate = contentChild(AiRunnerResultDirective);

  readonly phase = signal<Phase>('input');
  readonly run = signal<AiRun | null>(null);
  readonly error = signal<string | null>(null);
  readonly starting = signal(false);
  readonly prompt = signal('');
  readonly files = signal<File[]>([]);
  readonly filesError = signal<string | null>(null);
  readonly pipelines = signal<AiPipeline[]>([]);
  readonly pipelineId = signal<string | null>(null);
  readonly pipelinesLoading = signal(false);
  readonly messages = signal<AiChatMessage[]>([]);
  readonly sent = signal<{ input: AiRunnerInput; fileNames: string[] } | null>(null);
  readonly bindingPreview = signal<AiResultBindingPreview[]>([]);
  readonly applying = signal(false);
  readonly applied = signal(false);
  readonly sentOpen = signal(false);
  readonly variantKey = signal<string | null>(null);
  readonly actionStates = signal<AiResultActionState[]>([]);
  /** Texto editado por ação (chave da ação), para as que declaram `content`. */
  readonly actionEdits = signal<Record<string, string>>({});
  /** Ação com o editor aberto. */
  readonly editingAction = signal<string | null>(null);
  /** Execução que bloqueou o disparo (chamado já tem uma ativa): dá para acompanhá-la. */
  readonly busyRunId = signal<string | null>(null);
  /** Execuções anteriores para consulta (`config.history`). */
  readonly history = signal<AiRun[]>([]);
  /** Lista de resultados anteriores aberta (painel sobre o corpo, a partir do rodapé). */
  readonly historyOpen = signal(false);
  /** Cartão da execução retomada quando a pipeline dela não está na lista de escolha. */
  private readonly resumedVariant = signal<AiRunnerVariant | null>(null);
  readonly actionsRunning = signal(false);
  readonly rawView = signal(false);
  readonly missingPipeline = signal<string | null>(null);
  readonly optionValues = signal<AiRunnerOptionValues>({});
  /** Registro escolhido em cada campo `record` (o valor guardado é só o id). */
  readonly recordChoices = signal<Partial<Record<string, AiRunnerChoice | null>>>({});
  readonly recordSuggestions = signal<Partial<Record<string, AiRunnerChoice[]>>>({});
  /** Painel de recusa do resultado aberto (motivo obrigatório antes de voltar à revisão). */
  readonly rejecting = signal(false);

  private readonly lastSequenceByRun = new Map<string, number>();
  private refetchToken = 0;

  readonly mode = computed(() => this.config().mode);
  readonly live = computed(() => this.config().live !== false);
  readonly showSteps = computed(() => this.config().showSteps !== false);
  readonly isChat = computed(() => this.mode() === 'chat');
  readonly hasPrompt = computed(() => this.mode() !== 'preset');
  readonly filesEnabled = computed(() => !!this.config().files?.enabled);
  readonly maxSizeMb = computed(() => this.config().files?.maxSizeMb ?? this.d().maxFileMb);
  readonly pipelineTarget = computed(() => {
    const target = this.config().target;
    return target.kind === 'pipeline' ? target : null;
  });
  readonly selectable = computed(() => !!this.pipelineTarget()?.selectable);
  readonly unavailable = computed(() =>
    this.backend.supports(this.config().target) ? null : this.t().modelUnavailable
  );
  readonly selectedPipeline = computed(
    () => this.pipelines().find(p => p.id === this.pipelineId()) ?? null
  );
  readonly running = computed(() => this.phase() === 'running');
  /** Modo lista: `target.pipelines` vira os cartões, complementados por `variants`. */
  readonly listMode = computed(() => !!this.pipelineTarget()?.pipelines?.length);
  readonly variants = computed(() => {
    const target = this.pipelineTarget();
    return target?.pipelines?.length
      ? buildPipelineVariants(target.pipelines, this.pipelines(), this.config().variants)
      : (this.config().variants ?? []);
  });
  readonly variant = computed(
    () => findVariant(this.variants(), this.variantKey()) ?? this.resumedVariant()
  );
  readonly shownOptions = computed(() =>
    visibleOptions(this.config().options ?? [], this.optionValues(), this.variantKey())
  );
  readonly optionsError = computed(() => validateOptions(this.shownOptions(), this.optionValues()));
  readonly resultFormat = computed<AiResultFormat>(
    () => this.variant()?.resultFormat ?? this.config().resultFormat ?? 'markdown'
  );
  readonly resultView = computed(() => this.variant()?.resultView ?? this.config().resultView);
  readonly resultContext = computed<AiResultContext | null>(() => {
    const run = this.run();
    if (!run || this.phase() !== 'done') return null;
    return {
      run,
      output: this.output(),
      json: this.resultJson(),
      format: this.resultFormat(),
      variant: this.variant(),
    };
  });
  readonly fieldViews = computed(() => {
    const fields = this.resultView()?.fields;
    return fields?.length ? buildFieldViews(fields, this.resultJson()) : [];
  });
  readonly viewMarkdown = computed(() => {
    const ctx = this.resultContext();
    const toMarkdown = this.resultView()?.markdown;
    if (!ctx || !toMarkdown) return null;
    try {
      return toMarkdown(ctx);
    } catch {
      return null;
    }
  });
  /** Há apresentação personalizada (template, campos ou markdown) e ainda não se pediu o bruto. */
  readonly customView = computed(
    () =>
      !this.rawView() &&
      (!!this.resultTemplate() || this.fieldViews().length > 0 || this.viewMarkdown() !== null)
  );
  readonly canToggleRaw = computed(
    () =>
      this.resultView()?.showRaw !== false &&
      (!!this.resultTemplate() || this.fieldViews().length > 0 || this.viewMarkdown() !== null)
  );
  readonly actionPolicy = computed(
    () => this.variant()?.actionPolicy ?? this.config().actionPolicy ?? { mode: 'choose' as const }
  );
  readonly actionsLocked = computed(() => this.actionPolicy().mode === 'fixed');
  readonly pendingActions = computed(() =>
    this.actionStates().filter(state => state.selected && state.status !== 'done')
  );
  /**
   * Dados mostrados em "Enviado": os que o servidor gravou na execução (já mascarados e, no
   * chamado, montados por ele) quando o detalhe chega; antes disso, os que o navegador mandou.
   */
  readonly sentData = computed(() => {
    const serverInput = this.run()?.input as { data?: unknown } | null | undefined;
    return serverInput?.data ?? this.sent()?.input.data ?? null;
  });
  /** Nome do arquivo baixado: ia-<pipeline>-<chamado ou data>. */
  readonly exportName = computed(() => {
    const pipeline = this.run()?.pipeline?.slug ?? this.variant()?.key ?? 'resultado';
    const ref = this.config().refId ? this.descriptionText().replace(/\D/g, '') : '';
    return `ia-${pipeline}-${ref || new Date().toISOString().slice(0, 10)}`;
  });
  /** Avaliação e recusa: execução de pipeline concluída e só para quem pode executar. */
  readonly canRate = computed(
    () =>
      this.phase() === 'done' &&
      this.access().canRun &&
      !!this.run()?.id &&
      this.config().target.kind === 'pipeline' &&
      this.config().feedback !== false
  );
  /** Com variantes, "Nova execução" volta à escolha mesmo no modo `preset`. */
  readonly canRunAgain = computed(() => this.mode() !== 'preset' || this.variants().length > 0);
  readonly steps = computed(() => sortRunSteps(this.run()?.steps));
  /** Percentual: etapas distintas concluídas sobre o total de etapas da pipeline. */
  readonly percent = computed(() => {
    const run = this.run();
    if (!run) return 0;
    if (FINISHED_RUN.includes(run.status)) return 100;
    const total = this.selectedPipeline()?.steps_count ?? this.selectedPipeline()?.steps?.length;
    const done = new Set(
      (run.steps ?? []).filter(step => step.status === 'completed').map(step => step.step_order)
    ).size;
    if (!total) return run.status === 'running' ? 50 : 5;
    return Math.min(95, Math.max(5, Math.round((done / total) * 100)));
  });
  readonly output = computed(() => {
    const run = this.run();
    return run ? resultText(run.output, run.output_data) : '';
  });
  readonly resultJson = computed(() => {
    const run = this.run();
    return run ? extractResultJson(run.output_data, run.output) : null;
  });
  readonly access = computed(() => resolveAiRunnerAccess(this.accessPolicy, this.config()));
  /** Aviso de permissão negada (execução bloqueada, modal só leitura). */
  readonly denied = computed(() => {
    const access = this.access();
    return access.canRun ? null : (access.denyReason ?? this.t().denied);
  });
  /**
   * Sem permissão de executar e com histórico (chamado): o modal vira consulta — esconde opções,
   * observação e "Analisar", e lista os resultados direto no corpo.
   */
  readonly readOnly = computed(() => !!this.denied() && !!this.config().history);
  readonly canSubmit = computed(() => {
    if (this.starting() || this.running() || this.unavailable() || this.denied()) return false;
    if (this.config().target.kind === 'pipeline' && !this.pipelineId()) return false;
    if (this.variants().length && !this.variant()) return false;
    if (this.optionsError()) return false;
    if (this.filesError()) return false;
    const required = this.isChat() || this.config().prompt?.required;
    return !(this.hasPrompt() && required && !this.prompt().trim());
  });

  constructor() {
    // Abrir o modal reinicia o estado a partir da configuração.
    effect(() => {
      if (!this.visible()) return;
      const config = this.config();
      untracked(() => this.reset(config));
    });

    // Reverb: só enquanto o modal está aberto e há uma execução sendo acompanhada.
    effect(onCleanup => {
      const id = this.visible() && this.running() ? this.run()?.id : null;
      if (!id) return;
      untracked(() => this.subscribeRun(id, onCleanup));
    });
  }

  private reset(config: AiRunnerConfig): void {
    this.refetchToken++;
    this.phase.set('input');
    this.run.set(null);
    this.error.set(null);
    this.files.set([]);
    this.filesError.set(null);
    this.messages.set([]);
    this.sent.set(null);
    this.bindingPreview.set([]);
    this.applied.set(false);
    this.sentOpen.set(false);
    this.actionStates.set([]);
    this.actionEdits.set({});
    this.editingAction.set(null);
    this.busyRunId.set(null);
    this.resumedVariant.set(null);
    this.history.set([]);
    this.historyOpen.set(false);
    this.rawView.set(false);
    this.missingPipeline.set(null);
    this.variantKey.set(
      config.target.kind === 'pipeline' && config.target.pipelines?.length
        ? null
        : (findVariant(config, config.defaultVariant)?.key ?? null)
    );
    this.optionValues.set(
      initialOptionValues(config.options ?? [], loadRememberedOptions(config.storageKey))
    );
    this.recordChoices.set({});
    this.recordSuggestions.set({});
    this.rejecting.set(false);
    this.prompt.set(config.prompt?.initial ?? '');
    this.lastSequenceByRun.clear();

    const target = config.target;
    this.pipelineId.set(target.kind === 'pipeline' ? (target.pipelineId ?? null) : null);
    const loading =
      target.kind !== 'pipeline'
        ? Promise.resolve()
        : target.pipelines?.length
          ? this.loadPipelineList(target.pipelines, config.defaultVariant)
          : this.loadPipelines(target.selectable, target.allowedPipelineIds, target.pipelineSlug);

    if (config.history) loading.then(() => this.loadHistory(config));

    const resuming = config.resume
      ? loading.then(() => this.resumeRun(config))
      : Promise.resolve(false);

    // Com opções de escolha (variantes ou lista), só dispara sozinho se já houver uma escolhida.
    const autoStart = config.autoStart ?? config.mode === 'preset';
    if (autoStart) {
      resuming.then(resumed => {
        if (resumed) return;
        if (this.variants().length && !this.variant()) return;
        if (this.optionsError()) return;
        this.submit();
      });
    }
  }

  /**
   * Modo lista: carrega cada pipeline em paralelo. As que faltarem (inexistentes, sem permissão ou
   * desabilitadas) viram um aviso, sem impedir as demais.
   */
  private async loadPipelineList(
    refs: readonly AiRunnerPipelineRef[],
    defaultKey?: string
  ): Promise<void> {
    this.pipelinesLoading.set(true);
    const results = await Promise.allSettled(
      refs.map(async raw => {
        const ref = pipelineRef(raw);
        let id = ref.id;
        if (!id && ref.slug) {
          id = (await this.backend.listPipelines(ref.slug)).find(p => p.slug === ref.slug)?.id;
        }
        if (!id) throw new Error('não encontrada');
        const pipeline = await this.backend.getPipeline(id);
        if (!pipeline?.enabled) throw new Error('desabilitada');
        return pipeline;
      })
    );
    const loaded = results.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []));
    const missing = refs
      .filter((_, i) => results[i].status === 'rejected')
      .map(raw => pipelineRef(raw).slug ?? pipelineRef(raw).id);
    this.pipelines.set(loaded);
    if (missing.length) {
      this.missingPipeline.set(
        this.pipelineTarget()?.missingMessage ??
          `Pipeline(s) indisponível(is) neste grupo: ${missing.join(', ')}.`
      );
    }
    const preset = defaultKey ? findVariant(this.variants(), defaultKey) : null;
    if (preset) this.selectVariant(preset.key);
    this.pipelinesLoading.set(false);
  }

  /** Retoma a execução de `config.resume` (acompanha se ativa; mostra o resultado se concluída). */
  private async resumeRun(config: AiRunnerConfig): Promise<boolean> {
    try {
      const found = await config.resume?.();
      if (!found || !this.visible() || this.config() !== config) return false;
      this.attachRun(found);
      return true;
    } catch {
      return false;
    }
  }

  private async loadHistory(config: AiRunnerConfig): Promise<void> {
    try {
      const runs = (await config.history?.()) ?? [];
      if (this.config() === config) this.history.set(runs);
    } catch {
      // Sem histórico: o modal funciona igual, só não lista os resultados anteriores.
    }
  }

  /** Abre o resultado de uma execução anterior. */
  openHistoryRun(run: AiRun): void {
    if (this.starting() || this.running()) return;
    this.attachRun(run);
  }

  /** Descrição/rótulo que o cartão corta: só nesses o mouse mostra o cartão completo. */
  variantTruncated(item: AiRunnerVariant): boolean {
    return (item.description?.length ?? 0) > 70 || (item.label?.length ?? 0) > 24;
  }

  historyWhen(run: AiRun): string {
    const date = run.finished_at ?? run.started_at ?? run.created_at;
    return date ? new Date(date).toLocaleString('pt-BR') : '';
  }

  historyStatus(run: AiRun): string {
    const labels = this.t();
    const byStatus: Record<string, string> = {
      pending: labels.statusPending,
      running: labels.statusRunning,
      completed: labels.statusCompleted,
      failed: labels.statusFailed,
      cancelled: labels.statusCancelled,
    };
    return byStatus[run.status] ?? run.status;
  }

  /** Passa a acompanhar uma execução que já existe (retomada ou a que bloqueou o disparo). */
  private attachRun(run: AiRun): void {
    const pipelineId = run.pipeline_id ?? run.pipeline?.id ?? null;
    const listed = this.variants().find(v => v.pipelineId === pipelineId);
    if (listed) {
      this.variantKey.set(listed.key);
    } else if (pipelineId) {
      // Pipeline fora da escolha (ex.: só leitura): cartão só para apresentar o resultado.
      const slug = run.pipeline?.slug;
      const extra = [
        ...(this.config().resultVariants ?? []),
        ...(this.config().variants ?? []),
      ].find(v => v.pipeline === slug || v.pipeline === pipelineId);
      this.resumedVariant.set({
        ...extra,
        key: pipelineId,
        label: extra?.label ?? run.pipeline?.name ?? this.t().unknownRun,
        pipeline: slug ?? undefined,
        pipelineId,
      });
      this.variantKey.set(pipelineId);
    }
    if (pipelineId) this.pipelineId.set(pipelineId);
    this.error.set(null);
    this.busyRunId.set(null);
    this.lastSequenceByRun.clear();
    this.run.set(run);
    this.phase.set('running');
    // Detalhe (etapas e saída) e, se já terminou, o resultado com as ações.
    this.refresh(run.id);
  }

  /** "Acompanhar" no aviso de chamado ocupado. */
  async followBusyRun(): Promise<void> {
    const id = this.busyRunId();
    if (!id) return;
    try {
      this.attachRun(await this.backend.getRun(id));
    } catch (error) {
      this.error.set(parseAiHttpError(error, this.t().loadPipelinesError).message);
    }
  }

  private async loadPipelines(
    selectable?: boolean,
    allowed?: string[],
    slug?: string
  ): Promise<void> {
    this.pipelinesLoading.set(true);
    try {
      let list: AiPipeline[];
      if (selectable) {
        list = await this.backend.listPipelines();
        if (allowed?.length) list = list.filter(p => allowed.includes(p.id));
      } else if (this.pipelineId()) {
        list = [await this.backend.getPipeline(this.pipelineId()!)];
      } else if (slug) {
        // A busca também casa nome e descrição: confere o slug exato.
        const found = (await this.backend.listPipelines(slug)).find(p => p.slug === slug);
        list = found ? [await this.backend.getPipeline(found.id)] : [];
        if (found) this.pipelineId.set(found.id);
        else {
          const target = this.pipelineTarget();
          this.missingPipeline.set(
            target?.missingMessage ??
              `A pipeline "${slug}" não está cadastrada ou está desabilitada neste grupo.`
          );
        }
      } else {
        list = [];
      }
      this.pipelines.set(list);
    } catch (error) {
      this.error.set(parseAiHttpError(error, this.t().loadPipelinesError).message);
    } finally {
      this.pipelinesLoading.set(false);
    }
  }

  private subscribeRun(id: string, onCleanup: (fn: () => void) => void): void {
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    const refetchSoon = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => this.refresh(id), 500);
    };

    // Reserva: sem aviso em tempo real no intervalo, consulta a execução (Reverb fora do ar,
    // canal não autorizado...). Cada aviso recebido adia a próxima consulta.
    const pollMs = this.config().pollFallbackMs ?? this.d().pollFallbackMs;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const schedulePoll = () => {
      if (!pollMs) return;
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = setTimeout(async () => {
        await this.refresh(id);
        if (this.running() && this.run()?.id === id) schedulePoll();
      }, pollMs);
    };
    schedulePoll();

    const subscription = this.backend.events().subscribe((event: AiRunRealtimeEvent) => {
      if (event.run?.id?.toLowerCase() !== id.toLowerCase()) return;
      schedulePoll();
      if (!shouldApplyRealtimeEvent(this.lastSequenceByRun, event)) return;
      const current = this.run();
      if (current) {
        this.run.set({
          ...applyRunEvent(current, event),
          steps: applyStepEvent(current.steps, event),
        });
      }
      if (shouldRefetchRunDetail(event)) refetchSoon();
    });

    // A execução pode ter andado entre o POST e a inscrição: busca uma vez.
    refetchSoon();

    onCleanup(() => {
      subscription.unsubscribe();
      if (refetchTimer) clearTimeout(refetchTimer);
      if (pollTimer) clearTimeout(pollTimer);
    });
  }

  private async refresh(id: string): Promise<void> {
    const token = this.refetchToken;
    try {
      const run = await this.backend.getRun(id);
      if (token !== this.refetchToken || this.run()?.id !== id) return;
      this.run.set(run);
      if (FINISHED_RUN.includes(run.status)) this.finish(run);
    } catch {
      // Fica o que o aviso mostrou; o próximo aviso tenta de novo.
    }
  }

  private finish(run: AiRun): void {
    if (this.phase() !== 'running') return;
    const ok = run.status === 'completed';
    this.phase.set(ok ? 'done' : 'error');

    if (this.isChat()) {
      this.messages.update(list => [
        ...list,
        ok
          ? { role: 'assistant', content: this.output() || '(sem resposta)', runId: run.id }
          : {
              role: 'assistant',
              content: run.error || this.t().runNotFinished,
              runId: run.id,
              error: true,
            },
      ]);
    }

    if (!ok) {
      this.error.set(
        run.error || (run.status === 'cancelled' ? this.t().runCancelled : this.t().runFailed)
      );
      this.failed.emit(run);
      this.hook(() => this.config().hooks?.onError?.(this.error() ?? run.error ?? '', run));
      return;
    }

    const bindings = this.config().resultBindings ?? [];
    if (bindings.length && this.access().canApply()) {
      this.bindingPreview.set(previewBindings(bindings, this.resultJson()));
      if (this.config().applyResult === 'auto') this.applyBindings();
    }

    const actions = (this.variant()?.resultActions ?? this.config().resultActions ?? []).filter(
      action => this.access().canApply(action)
    );
    const ctx = this.resultContext();
    if (actions.length && ctx) {
      this.actionStates.set(resolveActionStates(actions, this.actionPolicy(), ctx));
      const policy = this.actionPolicy();
      if (policy.mode === 'fixed' && policy.auto) this.applyActions();
    }
    this.completed.emit(run);
    this.hook(() => this.config().hooks?.onResult?.(run));
  }

  /** Hooks do host nunca derrubam o fluxo do modal. */
  private hook(fn: () => void): void {
    try {
      fn();
    } catch (e) {
      console.error('[ai-runner] hook falhou', e);
    }
  }

  async onFilesSelected(event: Event): Promise<void> {
    const inputEl = event.target as HTMLInputElement;
    const picked = Array.from(inputEl.files ?? []);
    inputEl.value = '';
    this.setFiles([...this.files(), ...picked]);
  }

  removeFile(index: number): void {
    this.setFiles(this.files().filter((_, i) => i !== index));
  }

  private setFiles(files: File[]): void {
    this.files.set(files);
    this.filesError.set(validateFiles(files, this.config().files?.maxFiles, this.maxSizeMb()));
  }

  private async resolveData(): Promise<AiRunnerData | undefined> {
    const data = this.config().data;
    return typeof data === 'function'
      ? await data({ options: this.optionValues(), variant: this.variant() })
      : data;
  }

  setOption(key: string, value: AiRunnerOptionValue): void {
    this.optionValues.update(values => ({ ...values, [key]: value }));
  }

  async searchRecord(key: string, query: string): Promise<void> {
    const option = this.config().options?.find(item => item.key === key);
    if (option?.type !== 'record') return;
    try {
      const found = await option.search(query ?? '');
      this.recordSuggestions.update(map => ({ ...map, [key]: found }));
    } catch {
      this.recordSuggestions.update(map => ({ ...map, [key]: [] }));
    }
  }

  pickRecord(key: string, choice: AiRunnerChoice | string | null): void {
    const picked = choice && typeof choice === 'object' ? choice : null;
    this.recordChoices.update(map => ({ ...map, [key]: picked }));
    this.setOption(key, picked?.value ?? null);
  }

  async submit(): Promise<void> {
    if (!this.canSubmit()) return;
    if (!this.backend.supports(this.config().target)) return;

    this.starting.set(true);
    this.error.set(null);
    this.bindingPreview.set([]);
    this.applied.set(false);
    this.actionStates.set([]);
    this.rawView.set(false);
    try {
      const config = this.config();
      const [data, files] = await Promise.all([
        this.resolveData(),
        Promise.all(this.files().map(fileToRunnerFile)),
      ]);
      const history = this.messages();
      const input = buildRunInput(
        config,
        this.prompt(),
        data,
        files,
        history,
        this.variant(),
        this.optionValues()
      );
      const hooked = await config.hooks?.beforeRun?.(input, this.slotContext());
      if (hooked === false) return;
      const finalInput = hooked ?? input;
      // Lembra a escolha que de fato foi usada (não cada clique).
      saveRememberedOptions(config.storageKey, config.options ?? [], this.optionValues());
      const target =
        config.target.kind === 'pipeline'
          ? { ...config.target, pipelineId: this.pipelineId() }
          : config.target;

      const run = await this.backend.start(target, finalInput);
      this.sent.set({ input: finalInput, fileNames: files.map(f => f.name) });
      if (this.isChat()) {
        this.messages.update(list => [...list, { role: 'user', content: this.prompt().trim() }]);
        this.prompt.set('');
        this.files.set([]);
      }
      this.lastSequenceByRun.clear();
      this.run.set(run);
      this.phase.set('running');
      this.runStarted.emit(run);
      this.hook(() => config.hooks?.afterRun?.(run));
      if (FINISHED_RUN.includes(run.status)) this.finish(run);
    } catch (error) {
      const parsed = parseAiHttpError(error, this.t().startError);
      // Teto mensal da pipeline: a mensagem do servidor já traz gasto e teto; diz onde ajustar.
      const message =
        parsed.code === AI_REJECT_CODES.budgetExhausted
          ? `${parsed.message} O teto é ajustado em IA > Pipelines, na seção "Gasto e qualidade".`
          : parsed.message;
      this.error.set(message);
      const busy = parsed.code === AI_REJECT_CODES.ticketBusy ? parsed.context?.['run_id'] : null;
      this.busyRunId.set(typeof busy === 'string' ? busy : null);
      this.hook(() => this.config().hooks?.onError?.(message));
    } finally {
      this.starting.set(false);
    }
  }

  async cancel(): Promise<void> {
    const run = this.run();
    if (!run || !this.running()) return;
    try {
      await this.backend.cancelRun(run.id);
    } catch (error) {
      this.error.set(parseAiHttpError(error, this.t().cancelError).message);
    }
  }

  async applyBindings(): Promise<void> {
    const run = this.run();
    if (!run || this.applying()) return;
    this.applying.set(true);
    const keys: string[] = [];
    try {
      if ((await this.config().hooks?.beforeApply?.(run)) === false) return;
      for (const item of this.bindingPreview().filter(preview => preview.found)) {
        await item.binding.apply(item.value, run);
        keys.push(item.binding.key);
      }
      this.applied.set(true);
      this.resultApplied.emit({ run, keys });
    } catch (error) {
      this.error.set(parseAiHttpError(error, this.t().applyError).message);
    } finally {
      this.applying.set(false);
    }
  }

  selectVariant(key: string): void {
    if (this.starting() || this.running()) return;
    this.variantKey.set(key);
    // No modo lista, a variante é a própria pipeline.
    const pipelineId = findVariant(this.variants(), key)?.pipelineId;
    if (pipelineId) this.pipelineId.set(pipelineId);
  }

  toggleAction(key: string): void {
    this.actionStates.update(list =>
      list.map(state =>
        state.action.key === key && !state.locked && state.status !== 'done'
          ? { ...state, selected: !state.selected }
          : state
      )
    );
  }

  /**
   * Aplica as ações marcadas, em sequência. Uma falha fica registrada na própria ação e não
   * impede as demais nem fecha o modal; repetir aplica só as que não deram certo.
   */
  /** Abre/fecha o editor do conteúdo de uma ação; ao abrir, parte do texto original. */
  toggleEdit(state: AiResultActionState): void {
    const key = state.action.key;
    if (this.editingAction() === key) {
      this.editingAction.set(null);
      return;
    }
    const ctx = this.resultContext();
    if (!this.actionEdits()[key] && ctx && state.action.content) {
      const original = state.action.content(ctx) ?? '';
      this.actionEdits.update(map => ({ ...map, [key]: original }));
    }
    this.editingAction.set(key);
  }

  setActionEdit(key: string, value: string): void {
    this.actionEdits.update(map => ({ ...map, [key]: value }));
  }

  async applyActions(): Promise<void> {
    const ctx = this.resultContext();
    if (!ctx || this.actionsRunning()) return;
    this.actionsRunning.set(true);
    const done: string[] = [];
    const patch = (key: string, change: Partial<AiResultActionState>) =>
      this.actionStates.update(list =>
        list.map(state => (state.action.key === key ? { ...state, ...change } : state))
      );
    try {
      if ((await this.config().hooks?.beforeApply?.(ctx.run)) === false) return;
      for (const state of this.pendingActions()) {
        const key = state.action.key;
        patch(key, { status: 'running', error: null });
        try {
          await state.action.run(ctx, this.actionEdits()[key]);
          patch(key, { status: 'done' });
          done.push(key);
        } catch (error) {
          // Erro lançado pela própria ação (sem `status` HTTP) traz a mensagem pronta.
          const own = error instanceof Error && !('status' in error) ? error.message : null;
          patch(key, {
            status: 'error',
            error: own || parseAiHttpError(error, this.t().finishError).message,
          });
        }
      }
      if (done.length) this.actionsApplied.emit({ run: ctx.run, keys: done });
    } finally {
      this.actionsRunning.set(false);
    }
  }

  /**
   * Recusa gravada (avaliação "não útil" com `rejected_actions`): nenhuma ação é aplicada e o
   * modal volta para a revisão, com o motivo escrito na opção `rejectFeedbackOption` para a próxima
   * execução levar em conta.
   */
  onRejected(saved: AiRunFeedbackSaved): void {
    this.actionStates.update(list =>
      list.map(state => (state.locked ? state : { ...state, selected: false }))
    );
    const key = this.config().rejectFeedbackOption;
    if (key) {
      const previous = this.optionValues()[key];
      this.setOption(
        key,
        rejectionInstruction(
          saved.reasonLabel,
          saved.payload.reason_text,
          typeof previous === 'string' ? previous : null
        )
      );
    }
    this.again();
  }

  /** Volta à entrada mantendo prompt, arquivos e variante (modos sem chat). */
  again(): void {
    const previous = this.run();
    if (previous && this.phase() !== 'running') {
      this.hook(() => this.config().hooks?.onDiscard?.(previous));
    }
    this.refetchToken++;
    if (this.config().history) this.loadHistory(this.config());
    this.actionEdits.set({});
    this.editingAction.set(null);
    this.resumedVariant.set(null);
    this.rejecting.set(false);
    this.phase.set('input');
    this.run.set(null);
    this.error.set(null);
    this.actionStates.set([]);
    this.bindingPreview.set([]);
  }

  onChatKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  /** Com `config.history`, "Fechar" num resultado volta à lista; fechar o modal é o 2º clique. */
  close(): void {
    if (
      this.config().history &&
      !this.running() &&
      (this.phase() === 'done' || this.phase() === 'error')
    ) {
      this.again();
      return;
    }
    this.closeModal();
  }

  /** Fecha o modal de vez (X, Esc e fundo). */
  closeModal(): void {
    this.visibleChange.emit(false);
  }

  formatSize(bytes: number): string {
    return bytes >= 1024 * 1024
      ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
}
